import { useMemo, useRef } from "react";
import { Editor } from "@/v2/components/editor/Editor";
import {
  BriefEditorProps,
  MentionItem,
  MentionListRef,
} from "./BriefEditor.model";
import { FormField } from "@/v2/components/form/form-schema";
import { Skill } from "@/v2/entities/skill/Skill.model";
import { ReactRenderer } from "@tiptap/react";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";
import type { SuggestionProps } from "@tiptap/suggestion";
import styles from "./BriefEditor.module.scss";
import { BriefMentionList } from "./BriefMentionList";

const TICKET_FIELD_MENTIONS: MentionItem[] = [
  {
    id: "ticket.subject",
    name: "Ticket Subject",
    email: "Ticket Subject",
    role: "Ticket Field",
  },
  {
    id: "ticket.description",
    name: "Ticket Description",
    email: "Ticket Description",
    role: "Ticket Field",
  },
  {
    id: "ticket.status",
    name: "Ticket Status",
    email: "Ticket Status",
    role: "Ticket Field",
  },
  {
    id: "ticket.priority",
    name: "Ticket Priority",
    email: "Ticket Priority",
    role: "Ticket Field",
  },
  {
    id: "ticket.type",
    name: "Ticket Type",
    email: "Ticket Type",
    role: "Ticket Field",
  },
  {
    id: "ticket.tags",
    name: "Ticket Tags",
    email: "Ticket Tags",
    role: "Ticket Field",
  },
  {
    id: "ticket.assignee_email",
    name: "Ticket Assignee Email",
    email: "Ticket Assignee Email",
    role: "Ticket Field",
  },
  {
    id: "ticket.requester_email",
    name: "Ticket Requester Email",
    email: "Ticket Requester Email",
    role: "Ticket Field",
  },
];

function buildMentionItems(
  skills: Skill[],
  customFields: FormField[],
  enrichments: FormField[]
): MentionItem[] {
  return [
    ...skills.map((s) => ({
      id: s.id,
      name: s.name,
      email: s.name,
      role: "Skill",
    })),
    ...customFields.map((f) => ({
      id: f.id,
      name: f.label,
      email: f.label,
      role: "Custom Field",
    })),
    ...enrichments.map((e) => ({
      id: e.id,
      name: e.label,
      email: e.label,
      role: "Enrichment",
    })),
    ...TICKET_FIELD_MENTIONS,
  ];
}

function filterMentionItems(items: MentionItem[], query: string): MentionItem[] {
  if (!query) {
    return items;
  }

  const q = query.toLowerCase();
  return items.filter(
    (item) =>
      item.name.toLowerCase().includes(q) || item.role.toLowerCase().includes(q)
  );
}

export function BriefEditor({
  value,
  onChange,
  placeholder,
  skills = [],
  customFields = [],
  enrichments = [],
  dialogContentRef,
}: BriefEditorProps & { dialogContentRef?: React.RefObject<HTMLDivElement> }) {
  const mentionableItems = useMemo(
    () => buildMentionItems(skills, customFields, enrichments),
    [skills, customFields, enrichments]
  );
  const mentionableItemsRef = useRef(mentionableItems);
  mentionableItemsRef.current = mentionableItems;

  const mentionOptions = useMemo(() => {
    return {
      items: async ({ query }: { query: string }) => {
        return filterMentionItems(mentionableItemsRef.current, query);
      },

      render: () => {
        let component: ReactRenderer;
        let popup: HTMLElement;

        return {
          onStart: (props: SuggestionProps) => {
            component = new ReactRenderer(BriefMentionList, {
              props: {
                items: props.items,
                command: props.command,
              },
              editor: props.editor,
            });

            if (!props.clientRect) {
              return;
            }

            popup = document.createElement("div");
            popup.style.position = "absolute";
            popup.style.zIndex = "1000";
            popup.style.pointerEvents = "auto";
            popup.appendChild(component.element);
            document.body.appendChild(popup);

            const updatePosition = () => {
              if (!props.clientRect) return;

              const rect = props.clientRect();
              if (!rect) return;

              computePosition(
                {
                  getBoundingClientRect: () => rect,
                },
                popup,
                {
                  placement: "bottom-start",
                  middleware: [offset(8), flip(), shift({ padding: 8 })],
                }
              ).then(({ x, y }) => {
                popup.style.left = `${x}px`;
                popup.style.top = `${y}px`;
              });
            };

            updatePosition();
          },

          onUpdate(props: SuggestionProps) {
            component.updateProps({
              items: props.items,
              command: props.command,
            });

            if (!props.clientRect || !popup) {
              return;
            }

            const rect = props.clientRect();
            if (!rect) return;

            computePosition(
              {
                getBoundingClientRect: () => rect,
              },
              popup,
              {
                placement: "bottom-start",
                middleware: [offset(8), flip(), shift({ padding: 8 })],
              }
            ).then(({ x, y }) => {
              popup.style.left = `${x}px`;
              popup.style.top = `${y}px`;
            });
          },

          onKeyDown(props: { event: KeyboardEvent }) {
            if (props.event.key === "Escape") {
              if (popup) {
                popup.remove();
              }
              return true;
            }

            return (component.ref as MentionListRef)?.onKeyDown(props) ?? false;
          },

          onExit() {
            if (popup) {
              popup.remove();
            }
            if (component) {
              component.destroy();
            }
          },
        };
      },
    };
  }, []);

  return (
    <div className={styles.editorWrapper}>
      <Editor
        content={value}
        onChange={onChange}
        editable={true}
        enableMentions={true}
        mentionOptions={mentionOptions}
        placeholder={placeholder}
        showToolbar={true}
        appendTo={() => dialogContentRef?.current ?? document.body}
      />
    </div>
  );
}
